/**
 * Recurring (non-debt) expenses — rent, insurance, property tax, utilities,
 * food, etc. Replaces the single `essentialExpenses` number on
 * `MonthlyLedgerEntry` with categorised entities of varying frequencies.
 *
 * Survival-floor accuracy was lossy before: a March property-tax payment
 * pushed that month tight but the floor showed the same number every month.
 * With per-frequency expenses, the floor can show BOTH "average monthly"
 * (steady-state planning) and "this month's actual" (reality for spiky
 * months).
 */

export type RecurringExpenseCategory =
  | 'housing' // rent, HOA dues
  | 'property_tax'
  | 'insurance' // home, auto, life, umbrella
  | 'utilities' // gas, electric, water, internet, phone
  | 'food' // groceries, regular dining
  | 'transportation' // gas, transit, car maintenance, parking
  | 'healthcare' // premiums (if not payroll-deducted), copays, prescriptions
  | 'subscriptions' // streaming, software, gym
  | 'childcare' // daycare, after-school, tuition
  | 'education' // student fees, courses
  | 'household' // cleaning supplies, household goods
  | 'other'

export const RECURRING_EXPENSE_CATEGORIES: RecurringExpenseCategory[] = [
  'housing',
  'property_tax',
  'insurance',
  'utilities',
  'food',
  'transportation',
  'healthcare',
  'subscriptions',
  'childcare',
  'education',
  'household',
  'other',
]

export const RECURRING_EXPENSE_CATEGORY_LABELS: Record<RecurringExpenseCategory, string> = {
  housing: 'Housing',
  property_tax: 'Property Tax',
  insurance: 'Insurance',
  utilities: 'Utilities',
  food: 'Food',
  transportation: 'Transportation',
  healthcare: 'Healthcare',
  subscriptions: 'Subscriptions',
  childcare: 'Childcare',
  education: 'Education',
  household: 'Household',
  other: 'Other',
}

export type ExpenseFrequency = 'monthly' | 'quarterly' | 'semi_annual' | 'annual'

export const EXPENSE_FREQUENCY_LABELS: Record<ExpenseFrequency, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annual: 'Semi-Annual',
  annual: 'Annual',
}

/** Number of times per year an expense at this frequency hits */
export const FREQUENCY_OCCURRENCES_PER_YEAR: Record<ExpenseFrequency, number> = {
  monthly: 12,
  quarterly: 4,
  semi_annual: 2,
  annual: 1,
}

export interface RecurringExpense {
  id: string
  /** See `workspace.ts` — optional today, required once SaaS auth lands */
  workspaceId?: string
  name: string
  category: RecurringExpenseCategory
  /** Amount per occurrence (NOT per month for non-monthly frequencies) */
  amount: number
  frequency: ExpenseFrequency
  /**
   * Months (1-12) when this expense is billed. Required for non-monthly
   * frequencies. For monthly, it's implicitly all months and this field is
   * ignored. For quarterly: 4 months; semi-annual: 2 months; annual: 1 month.
   * Examples:
   *   - Property tax annual, paid in March: [3]
   *   - Auto insurance semi-annual, March and September: [3, 9]
   *   - HOA quarterly, Jan/Apr/Jul/Oct: [1, 4, 7, 10]
   */
  billingMonths?: number[]
  /** Hide retired expenses without deleting their history */
  isActive: boolean
  /** Whether this counts toward the Survival Floor (true for essentials, false for nice-to-haves) */
  isEssential: boolean
  notes?: string
  createdAt: string
  updatedAt: string
}
